// Colors Admin — Phase 15.2 PR 4b.
//
// Admin-only curator queue for the 14 canonical-color palette: review
// supplier→canonical color_mappings that the heuristic couldn't decide
// (~417 rows today: Heathers, food-named like Banana / Pistachio / Sangria,
// regional names, etc.). For each row, the curator picks the canonical
// bucket(s) via a checkbox grid; backend backfills product_variants in
// the same transaction so the storefront filter UI sees the change
// immediately.
//
// Multi-canonical: a single supplier color can map to N canonical buckets
// (e.g., "Navy Blue Plaid" → blue + multi). The schema stores
// canonical_color[] as an array; UI mirrors that with checkboxes.
//
// Sort: affected-variant count DESC (high-impact first), then color name
// ASC. ~417 rows fit in a single payload — no pagination today.
//
// Pattern mirrors CategoriesAdminPage — admin role-gate, TanStack Query,
// useMutation with optimistic-style cache invalidation, brand-locked
// palette throughout.

import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";

// 14 canonical colors per data-model.md. Order chosen for editorial
// scanning (warm to cool, neutrals last).
const CANONICAL_COLORS = [
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "purple",
  "pink",
  "brown",
  "beige",
  "white",
  "gray",
  "black",
  "multi",
  "other",
  // CEO 2026-10-08: a colour we cannot place is "misc" (shown as "Misc").
  "misc",
] as const;
type CanonicalColor = (typeof CANONICAL_COLORS)[number];

const CONFIDENCE_TONES: Record<
  PendingColorMapping["confidence"],
  "active" | "prospect" | "dormant" | "closed" | "warn" | "info"
> = {
  manual: "active",
  high: "active",
  medium: "info",
  low: "warn",
  pending: "warn",
};

const CONFIDENCE_LABELS: Record<PendingColorMapping["confidence"], string> = {
  manual: "Manual",
  high: "High",
  medium: "Medium",
  low: "Low",
  pending: "Pending",
};

interface PendingColorMapping {
  id: string;
  supplierSlug: string;
  supplierColorRaw: string;
  canonicalColors: CanonicalColor[];
  confidence: "manual" | "high" | "medium" | "low" | "pending";
  suggestedCanonicals: CanonicalColor[];
  affectedVariantCount: number;
  reviewedBy: string | null;
  reviewedAt: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface PendingResponse {
  rows: PendingColorMapping[];
}

export function ColorsAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;
  const queryClient = useQueryClient();
  const queueKey = useMemo(
    () => ["admin", "color-mappings", "pending"] as const,
    [],
  );

  const queueQ = useQuery({
    queryKey: queueKey,
    queryFn: () => api<PendingResponse>("/admin/color-mappings/pending"),
    enabled: isAdmin,
  });

  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Colors
        </h1>
        <p className="mt-3 text-sm text-brown-300">
          This admin surface is only visible to users with the admin role.
        </p>
      </div>
    );
  }

  const allRows = queueQ.data?.rows ?? [];
  const supplierSlugs = Array.from(
    new Set(allRows.map((r) => r.supplierSlug)),
  ).sort();
  const trimmedSearch = search.trim().toLowerCase();
  const visibleRows = allRows.filter((r) => {
    if (supplierFilter !== "all" && r.supplierSlug !== supplierFilter) return false;
    if (
      trimmedSearch.length > 0 &&
      !r.supplierColorRaw.toLowerCase().includes(trimmedSearch)
    )
      return false;
    return true;
  });

  const totalAffected = allRows.reduce((s, r) => s + r.affectedVariantCount, 0);
  const visibleAffected = visibleRows.reduce(
    (s, r) => s + r.affectedVariantCount,
    0,
  );

  return (
    <div className="mx-auto w-full space-y-8">
      {/* Header */}
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Admin
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Colors
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-brown-400">
          Match each supplier color to an Other color so customers can
          filter the storefront by "Black" or "Blue" instead of "Heather
          Charcoal" or "Deep Forest." Anything still pending in the queue
          below stays out of the storefront filter until it has a match.
        </p>
      </div>

      {/* Stats + filters */}
      <div className="flex flex-wrap items-center gap-3 border-y border-brown-50 py-4">
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Pending
          </span>
          <span className="font-display text-2xl font-bold text-brown-500">
            {allRows.length}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            Variants affected
          </span>
          <span className="font-display text-2xl font-bold text-brown-500">
            {totalAffected.toLocaleString()}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by color name…"
            className="w-56"
          />
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
          >
            <option value="all">All suppliers</option>
            {supplierSlugs.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading / error / empty / list */}
      {queueQ.isLoading && (
        <p className="text-sm text-brown-300">Loading curator queue…</p>
      )}
      {queueQ.isError && (
        <p className="text-sm text-rust-600">Could not load the queue.</p>
      )}
      {!queueQ.isLoading && !queueQ.isError && allRows.length === 0 && (
        <p className="text-sm text-brown-300">
          The curator queue is empty — every supplier color has been assigned.
        </p>
      )}
      {!queueQ.isLoading && allRows.length > 0 && visibleRows.length === 0 && (
        <p className="text-sm text-brown-300">
          No rows match the current filter.
          {(search.length > 0 || supplierFilter !== "all") && " Clear it to see all pending rows."}
        </p>
      )}

      {visibleRows.length > 0 && (
        <>
          <p className="text-xs text-brown-300">
            Showing {visibleRows.length.toLocaleString()} of{" "}
            {allRows.length.toLocaleString()} rows · {visibleAffected.toLocaleString()}{" "}
            variants in view
          </p>
          <div className="space-y-3">
            {visibleRows.map((row) => (
              <ColorMappingCard
                key={row.id}
                row={row}
                onAssigned={() => {
                  void queryClient.invalidateQueries({ queryKey: queueKey });
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Card per mapping ─────────────────────────────────────────────────────

function ColorMappingCard(props: {
  row: PendingColorMapping;
  onAssigned: () => void;
}) {
  const { row, onAssigned } = props;
  const [selected, setSelected] = useState<Set<CanonicalColor>>(
    new Set(row.canonicalColors),
  );
  const [error, setError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: async (canonicalColors: CanonicalColor[]) =>
      api<{ mapping: PendingColorMapping; variants_updated: number }>(
        `/admin/color-mappings/${row.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ canonical_colors: canonicalColors }),
        },
      ),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onAssigned();
    },
  });

  const toggle = (c: CanonicalColor): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  };

  const applySuggestion = (): void => {
    setSelected(new Set(row.suggestedCanonicals));
  };

  const hasChanges = !sameSet(selected, new Set(row.canonicalColors));

  return (
    <div className="rounded-lg border border-brown-100 bg-white p-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              {row.supplierSlug}
            </span>
            <StatusPill tone={CONFIDENCE_TONES[row.confidence]}>
              {CONFIDENCE_LABELS[row.confidence]}
            </StatusPill>
          </div>
          <div className="mt-1 font-display text-xl font-bold text-brown-500">
            {row.supplierColorRaw}
          </div>
          <div className="mt-1 text-xs text-brown-300">
            {row.affectedVariantCount.toLocaleString()}{" "}
            variant{row.affectedVariantCount === 1 ? "" : "s"} affected
          </div>
        </div>
        {row.suggestedCanonicals.length > 0 && (
          <button
            type="button"
            onClick={applySuggestion}
            className="inline-flex items-center gap-1 rounded-full border border-peach-300 bg-peach-50 px-3 py-1 text-xs font-semibold text-brown-500 hover:bg-peach-100"
            title="Apply heuristic suggestion"
          >
            <Sparkles className="h-3 w-3" />
            Suggest: {row.suggestedCanonicals.join(", ")}
          </button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-4 md:grid-cols-7">
        {CANONICAL_COLORS.map((c) => (
          <CanonicalCheckbox
            key={c}
            value={c}
            checked={selected.has(c)}
            onChange={() => toggle(c)}
            disabled={assign.isPending}
          />
        ))}
      </div>

      {error && <p className="mt-3 text-sm text-rust-600">{error}</p>}

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-brown-300">
          {selected.size === 0
            ? "No canonical colors selected"
            : `${selected.size} canonical${selected.size === 1 ? "" : "s"} selected`}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSelected(new Set(row.canonicalColors))}
            disabled={!hasChanges || assign.isPending}
          >
            Reset
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={assign.isPending || !hasChanges}
            onClick={() => assign.mutate(Array.from(selected))}
          >
            {assign.isPending ? "Saving…" : "Assign"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CanonicalCheckbox(props: {
  value: CanonicalColor;
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
}) {
  const { value, checked, onChange, disabled } = props;
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm font-semibold capitalize transition-colors",
        checked
          ? "border-peach-400 bg-peach-50 text-brown-500"
          : "border-brown-100 bg-white text-brown-400 hover:bg-cream-50",
        disabled && "pointer-events-none opacity-60",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="h-3.5 w-3.5 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
      />
      {value}
    </label>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────

function sameSet(a: Set<CanonicalColor>, b: Set<CanonicalColor>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Save failed. Try again or check your connection.";
}
